import { useCallback, useState } from 'react';

import { getLocationStep, TObjectType, TReason } from './getLocationStep';
import { ILocation } from '../../../types/location';
import { getSearchType } from './getSearchType';
import { IGeoStep, TGeoExtras } from '../types';
import { useRegisterBackInterceptor } from './useRegisterBackInterceptor';
import { TAnswersState } from '../../FormContainer/types/internal';
import { useFormTracking } from '../../../utils/useFormTracking';
import { EStepField } from '../constants/stepField';
import { getExtraStepsByType } from './getExtraStepsByType';

export interface IUseStepsProps {
  answersState: TAnswersState;
  geoExtras: TGeoExtras;

  onSubmit(): void;

  registerBackInterceptor(callback: () => boolean): void;

  unRegisterBackInterceptor(): void;
}
export const useSteps = ({
  answersState,
  geoExtras,
  onSubmit,
  registerBackInterceptor,
  unRegisterBackInterceptor,
}: IUseStepsProps) => {
  const reason = answersState[geoExtras?.reasonField]?.value as TReason;
  const objectType = answersState[geoExtras?.objectTypeField]?.value as TObjectType;
  const { trackPrevStep } = useFormTracking();
  const [steps, setSteps] = useState<IGeoStep[]>([getLocationStep(reason, objectType)]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const updateSteps = useCallback(
    (location: ILocation | null, initialStepField?: EStepField) => {
      const [locationStep] = steps;

      if (!location) {
        setSteps([locationStep]);

        return;
      }
      const searchType = getSearchType(location);
      const extraSteps = getExtraStepsByType(objectType, searchType);

      const newSteps = [locationStep, ...extraSteps];
      if (initialStepField) {
        const newStepIndex = newSteps.findIndex(({ field }) => field === initialStepField);
        setCurrentStepIndex(newStepIndex);
      }

      setSteps(newSteps);
    },
    [steps, setSteps, objectType],
  );

  const nextStep = useCallback(() => {
    if (currentStepIndex === steps.length - 1) {
      onSubmit();

      return;
    }
    setCurrentStepIndex(currentStepIndex + 1);
  }, [steps, setCurrentStepIndex, currentStepIndex, onSubmit]);

  const prevStep = useCallback(() => {
    if (currentStepIndex === 0) {
      return true;
    }

    trackPrevStep(steps[currentStepIndex].field);

    setCurrentStepIndex(currentStepIndex - 1);

    return false;
  }, [setCurrentStepIndex, currentStepIndex, trackPrevStep, steps]);

  useRegisterBackInterceptor({
    registerBackInterceptor,
    unRegisterBackInterceptor,
    prevStep,
  });

  return {
    updateSteps,
    nextStep,
    prevStep,
    steps,
    currentStep: steps[currentStepIndex],
  };
};
